import type { ReactNode } from 'react';
import type { Classification, CountItem, Stats } from '@repo/shared';
import { CLASSIFICATION_LABEL, countryName, flagEmoji } from '../lib/format';
import styles from './Leaderboards.module.css';

interface BoardProps {
  title: string;
  items: CountItem[];
  /** Optional value renderer (e.g. flags for countries). */
  render?: (value: string) => ReactNode;
  /** Accent colour for the count bars. */
  accent?: string;
}

function Board({ title, items, render, accent = 'var(--green-dim)' }: BoardProps) {
  const max = items.reduce((m, it) => Math.max(m, it.count), 0) || 1;
  return (
    <section className={styles.board}>
      <h3 className={styles.title}>{title}</h3>
      {items.length === 0 ? (
        <p className={styles.empty}>—</p>
      ) : (
        <ol className={styles.list}>
          {items.map((it) => (
            <li key={it.value} className={styles.item}>
              <span
                className={styles.bar}
                style={{ width: `${(it.count / max) * 100}%`, background: accent }}
              />
              <span className={styles.name}>{render ? render(it.value) : it.value || '∅'}</span>
              <span className={styles.count}>{it.count}</span>
            </li>
          ))}
        </ol>
      )}
    </section>
  );
}

/** The always-funny leaderboards: top passwords, usernames, paths, countries. */
export function Leaderboards({ stats }: { stats: Stats }) {
  return (
    <div className={styles.grid}>
      <Board title="top passwords" items={stats.topPasswords} accent="var(--red)" />
      <Board title="top usernames" items={stats.topUsernames} accent="var(--green-dim)" />
      <Board title="top targeted paths" items={stats.topPaths} accent="var(--amber)" />
      <Board
        title="top origins"
        items={stats.topCountries}
        accent="var(--cyan)"
        render={(v) => (
          <span>
            {flagEmoji(v)} {countryName(v)}
          </span>
        )}
      />
      <Board
        title="attack types"
        items={stats.topClassifications}
        accent="var(--violet)"
        render={(v) => CLASSIFICATION_LABEL[v as Classification] ?? v}
      />
    </div>
  );
}
